/**
 * A simple storage utility to persist the token object.
 * In a larger app, this could be in its own file (e.g., './utils/storage.js').
 */
const storageService = {
  async saveAuthTokens(tokens) {
    const tokensJson = JSON.stringify(tokens);
    localStorage.setItem('google_auth_tokens', tokensJson);
  },
  async getAuthTokens() {
    const tokensJson = localStorage.getItem('google_auth_tokens');
    if (!tokensJson) {
      return null;
    }
    try {
      // Safely parse the stored JSON
      return JSON.parse(tokensJson);
    } catch (error) {
      console.error("Failed to parse auth tokens from storage", error);
      // If parsing fails, clear the corrupted data
      localStorage.removeItem('google_auth_tokens');
      return null;
    }
  },
  async clearAuthTokens() {
      localStorage.removeItem('google_auth_tokens');
  }
};


/**
 * Returns the correct Google OAuth client ID for the current domain.
 *
 * drlugha.com deployments use a separate Google Cloud project/client.
 * All other origins (localhost, tafiti.dev, Vercel previews) fall back
 * to the value in VITE_GOOGLE_CLIENT_ID.
 *
 * Google client IDs are not secrets — they appear in the page source
 * and in every OAuth redirect, so hardcoding both here is safe.
 */
function getGoogleClientId() {
  const hostname = window.location.hostname;
  if (hostname === 'drlugha.com' || hostname.endsWith('.drlugha.com')) {
    return '481110678302-hbe8972a09j80ijg9u87lcvstq4h491t.apps.googleusercontent.com';
  }
  return '807182761108-l9974t8ftodq74r4hpbhjc9ndhdckm2i.apps.googleusercontent.com';
}

class GoogleAuthService {
  constructor() {
    this.isInitialized = false;
    this.google = null; // The Google Identity Services library object
    this.tokens = null; // Holds the complete token object: { accessToken, expiresAt, obtainedAt, ... }
    this.currentUser = null;
    this.config = null;
    this.refreshIntervalId = null; // ID for the scheduled refresh timeout
    this.lastActivity = Date.now(); // Timestamp of last user activity
    this.isRefreshing = false; // A flag to prevent concurrent refresh attempts
    this.authStateCallbacks = [];
    this.silentRefreshAttempts = 0; // Track consecutive failed silent refresh attempts
    this.maxSilentRefreshAttempts = 3; // Maximum consecutive failures before giving up
    this.lastSilentRefreshAttempt = 0; // Timestamp of last attempt
  }

  restoreCachedUser() {
    const cachedUser = localStorage.getItem('polyglot-auth-user');
    if (!cachedUser) {
      return false;
    }

    try {
      this.currentUser = JSON.parse(cachedUser);
      this.notifyAuthStateChange(true);
      return true;
    } catch (error) {
      console.error('Failed to restore cached auth user:', error);
      localStorage.removeItem('polyglot-auth-user');
      return false;
    }
  }

  /**
   * Initializes the service, loads the Google library, and restores any saved sessions.
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    this.config = {
      clientId: getGoogleClientId(),
      scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'
    };

    await this.loadGoogleIdentityServices();
    this.setupActivityTracking(); //

    // Restore session from storage
    const savedTokens = await storageService.getAuthTokens();
    if (savedTokens && this.isTokenValid(savedTokens)) {
      this.tokens = savedTokens;
      this.scheduleTokenRefresh();
      if (savedTokens.accessToken) {
        await this.loadUserProfile({ preserveSessionOnFailure: true });
      } else {
        this.restoreCachedUser();
      }
    }

    // Initialize One Tap for the native browser sign-in overlay
    this.google.accounts.id.initialize({
      client_id: this.config.clientId,
      callback: (response) => this.handleOneTapCredential(response),
      auto_select: true,
      use_fedcm_for_button: true,
      button_auto_select: true,
      itp_support: true,
      cancel_on_tap_outside: false,
    });

    this.isInitialized = true;
  }

  /**
   * Dynamically loads the Google Identity Services script into the page.
   */
  async loadGoogleIdentityServices() {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts) {
        this.google = window.google;
        return resolve();
      }
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.google = window.google;
        resolve();
      };
      script.onerror = (err) => {
        console.error("Failed to load Google Identity Services script.", err);
        reject(new Error("Google script failed to load."));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Sets up listeners to track user activity, enabling smarter token refreshes.
   */
  setupActivityTracking() {
    const updateActivity = () => {
      this.lastActivity = Date.now();
    };
    ['click', 'keydown', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
    window.addEventListener('focus', () => {
      updateActivity();
      if (this.tokens && this.tokens.accessToken && this.shouldRefreshToken()) {
        this.attemptBackgroundRefresh();
      }
    });
  }

  /**
   * Checks if a token object is present and not expired (with a 5-minute buffer).
   */
  isTokenValid(tokens) {
    if (!tokens || !tokens.expiresAt) return false;
    return Date.now() < (tokens.expiresAt - (5 * 60 * 1000));
  }

  /**
   * Determines if a token should be refreshed (if it expires within 15 minutes).
   */
  shouldRefreshToken() {
    if (!this.tokens) return false;
    const timeToExpiry = this.tokens.expiresAt - Date.now();
    return timeToExpiry < (15 * 60 * 1000);
  }

  /**
   * Schedules the next silent refresh attempt based on the token's lifetime.
   */
  scheduleTokenRefresh() {
    if (this.refreshIntervalId) {
      clearTimeout(this.refreshIntervalId);
    }
    if (!this.tokens) return;

    // Aim to refresh when the token is 75% through its lifetime.
    const tokenLifetime = this.tokens.expiresAt - this.tokens.obtainedAt;
    const refreshTime = this.tokens.obtainedAt + (tokenLifetime * 0.75);
    const timeUntilRefresh = refreshTime - Date.now();

    if (timeUntilRefresh > 0) {
      this.refreshIntervalId = window.setTimeout(() => {
        this.attemptBackgroundRefresh();
      }, Math.min(timeUntilRefresh, 30 * 60 * 1000)); // Refresh at 75% or 30 mins, whichever is sooner.
    } else {
      // Token is already old; attempt to refresh it soon.
      this.attemptBackgroundRefresh();
    }
  }

  /**
   * Silently requests a new access token if the user has been active recently.
   */
  async attemptBackgroundRefresh() {
    if (this.isRefreshing || !this.config || !this.tokens) return null;

    // Check if we've exceeded max attempts
    if (this.silentRefreshAttempts >= this.maxSilentRefreshAttempts) {
      console.warn('⚠️ Max silent refresh attempts reached. Stopping retries (session preserved).');
      return null;
    }

    // Prevent rapid retry attempts (minimum 5 seconds between attempts)
    const timeSinceLastAttempt = Date.now() - this.lastSilentRefreshAttempt;
    if (timeSinceLastAttempt < 5000) {
      console.log('⏳ Too soon since last refresh attempt, skipping...');
      return null;
    }

    this.isRefreshing = true;
    this.lastSilentRefreshAttempt = Date.now();

    try {
      console.log('🔄 Attempting silent token refresh...');
      const refreshedToken = await this.requestNewTokenSilently();
      if (refreshedToken) {
        console.log('✅ Token refreshed silently in the background.');
        this.silentRefreshAttempts = 0; // Reset counter on success
        this.scheduleTokenRefresh(); // Schedule the *next* refresh
        return refreshedToken;
      } else {
        this.silentRefreshAttempts++;
        console.log(`❌ Silent background refresh failed (attempt ${this.silentRefreshAttempts}/${this.maxSilentRefreshAttempts}).`);
        return null;
      }
    } catch (error) {
      this.silentRefreshAttempts++;
      console.error(`Background token refresh error (attempt ${this.silentRefreshAttempts}/${this.maxSilentRefreshAttempts}):`, error);
      return null;
    } finally {
      this.isRefreshing = false;
    }
  }
  
  /**
   * Invokes the GIS library to get a new token without a user prompt.
   */
  async requestNewTokenSilently() {
    return new Promise((resolve) => {
      let isResolved = false;

      // Set a timeout to prevent hanging indefinitely
      const timeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          console.warn('⏱️ Silent token request timed out after 10 seconds');
          resolve(null);
        }
      }, 10000); // 10 second timeout

      try {
        const tokenClient = this.google.accounts.oauth2.initTokenClient({
          client_id: this.config.clientId,
          scope: this.config.scope,
          callback: async (response) => {
            clearTimeout(timeout);
            if (isResolved) return;
            isResolved = true;

            if (response.error) {
              console.log('Silent refresh error:', response.error, response.error_description);
              // Check for specific errors that indicate user interaction is needed
              if (response.error === 'immediate_failed' ||
                  response.error === 'access_denied' ||
                  response.error === 'interaction_required') {
                console.log('⚠️ User interaction required for token refresh');
              }
              return resolve(null);
            }
            const accessToken = await this.handleTokenResponse(response);
            resolve(accessToken);
          },
          error_callback: (error) => {
            clearTimeout(timeout);
            if (isResolved) return;
            isResolved = true;
            console.error('Silent token request error callback:', error);
            resolve(null);
          }
        });
        tokenClient.requestAccessToken({ prompt: 'none' }); // The key to a silent request
      } catch (error) {
        clearTimeout(timeout);
        if (isResolved) return;
        isResolved = true;
        console.error('Silent token request failed:', error);
        resolve(null);
      }
    });
  }

  /**
   * Processes a token response from Google, saves it, and loads the user profile.
   */
  async handleTokenResponse(response) {
    const now = Date.now();
    this.tokens = {
      accessToken: response.access_token,
      expiresAt: now + (response.expires_in * 1000),
      scope: this.config.scope,
      obtainedAt: now,
      lastRefresh: now
    };
    await storageService.saveAuthTokens(this.tokens);
    await this.loadUserProfile();
    return this.tokens.accessToken;
  }
  
  /**
   * Fetches the user's profile information using the access token.
   */
  async loadUserProfile(options = {}) {
    if (!this.tokens?.accessToken) return false;

    const { preserveSessionOnFailure = false } = options;

    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v1/userinfo?alt=json', {
        headers: { 'Authorization': `Bearer ${this.tokens.accessToken}` }
      });
      if (!response.ok) throw new Error(response.statusText);
      
      const profile = await response.json();
      this.currentUser = {
        id: profile.id,
        email: profile.email,
        name: profile.name,
        givenName: profile.given_name,
        imageUrl: profile.picture
      };

      localStorage.setItem('polyglot-auth-user', JSON.stringify(this.currentUser));

      if (profile.name) {
        localStorage.setItem('polyglot-user-name', profile.name);
      }

      this.notifyAuthStateChange(true);
      return true;
    } catch (error) {
      console.error('Error loading user profile:', error);

      if (preserveSessionOnFailure && this.restoreCachedUser()) {
        console.warn('Preserving cached auth session after profile reload failure.');
        return false;
      }

      // If the profile load fails and no cached user can be restored, clear the session.
      await this.signOut();
      return false;
    }
  }

  /**
   * Handles a credential response from Google One Tap.
   * Decodes the JWT id_token to get user info immediately, then silently
   * requests an access token for API calls.
   */
  async handleOneTapCredential(response) {
    try {
      // Decode the JWT payload (base64url) to extract user info
      const raw = response.credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(raw + '='.repeat((4 - raw.length % 4) % 4)));
      this.currentUser = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        givenName: payload.given_name,
        imageUrl: payload.picture
      };
      localStorage.setItem('polyglot-auth-user', JSON.stringify(this.currentUser));

      const now = Date.now();
      this.tokens = {
        accessToken: null,
        expiresAt: payload.exp * 1000,
        scope: this.config.scope,
        obtainedAt: now,
        lastRefresh: now,
      };
      await storageService.saveAuthTokens(this.tokens);
      this.silentRefreshAttempts = 0;
      this.scheduleTokenRefresh();
      this.notifyAuthStateChange(true);

      const tokenClient = this.google.accounts.oauth2.initTokenClient({
        client_id: this.config.clientId,
        scope: this.config.scope,
        callback: async (tokenResponse) => {
          if (!tokenResponse.error && tokenResponse.access_token) {
            await this.handleTokenResponse(tokenResponse);
          }
        },
      });
      tokenClient.requestAccessToken({ prompt: 'none' });
    } catch (error) {
      console.error('One Tap credential handling failed:', error);
    }
  }

  /**
   * Renders the official Google Sign-In button into the given container element.
   */
  renderSignInButton(container) {
    this.google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'filled_blue',
      size: 'large',
      text: 'signin_with',
      shape: 'pill',
      width: 300,
    });
  }

  /**
   * Displays the One Tap overlay and returns a promise that resolves to true if
   * the user signs in, or false if One Tap cannot be shown or is dismissed.
   */
  promptOneTap() {
    return new Promise((resolve) => {
      let resolved = false;

      const resolveOnce = (value) => {
        if (!resolved) {
          resolved = true;
          const idx = this.authStateCallbacks.indexOf(authListener);
          if (idx !== -1) this.authStateCallbacks.splice(idx, 1);
          resolve(value);
        }
      };

      const authListener = (isSignedIn) => {
        if (isSignedIn) resolveOnce(true);
      };
      this.authStateCallbacks.push(authListener);

      const timeout = setTimeout(() => resolveOnce(false), 30000);

      this.google.accounts.id.prompt();
    });
  }

  /**
   * Initiates an interactive sign-in process for the user.
   */
  async signIn() {
    if (!this.isInitialized) await this.initialize();

    return new Promise((resolve, reject) => {
      try {
        const tokenClient = this.google.accounts.oauth2.initTokenClient({
          client_id: this.config.clientId,
          scope: this.config.scope,
          callback: async (response) => {
            if (response.error) {
              return reject(new Error(response.error));
            }
            await this.handleTokenResponse(response);
            // Reset silent refresh attempts counter on successful sign-in
            this.silentRefreshAttempts = 0;
            resolve(this.getUserInfo());
          },
          error_callback: (error) => {
            reject(new Error(error?.message || 'Authentication failed'));
          }
        });
        // Use an interactive prompt for the user-initiated sign-in.
        tokenClient.requestAccessToken({ prompt: '' });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Signs the user out, revokes the token, and clears all local data.
   */
  async signOut() {
    if (this.refreshIntervalId) {
      clearTimeout(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
    if (this.tokens?.accessToken) {
      // Revoke the token on Google's side to invalidate it.
      this.google.accounts.oauth2.revoke(this.tokens.accessToken, () => {
        console.log('Token revoked.');
      });
    }
    this.tokens = null;
    this.currentUser = null;
    localStorage.removeItem('polyglot-auth-user');
    this.google?.accounts?.id?.disableAutoSelect();
    await storageService.clearAuthTokens();
    this.notifyAuthStateChange(false);
  }
  
  /**
   * Public method to check if the user is currently signed in.
   */
  isSignedIn() {
    return !!this.currentUser && !!this.tokens && this.isTokenValid(this.tokens);
  }

  /**
   * Public method to get the current user's profile information.
   */
  getUserInfo() {
    return this.currentUser;
  }
  
  /**
   * Public method to get the current access token (for backward compatibility).
   */
  getAccessToken() {
    return this.tokens?.accessToken || null;
  }

  /**
   * Validates the current session by checking token validity and potentially refreshing it.
   */
  async validateSession() {
    if (!this.tokens) {
      return false;
    }

    // If token is still valid, no need to refresh
    if (this.isTokenValid(this.tokens)) {
      return true;
    }

    // Try to refresh the token
    const refreshedToken = await this.attemptBackgroundRefresh();
    return !!refreshedToken;
  }

  /**
   * Public method for other services (like auth-guard) to get a valid token.
   * It will handle refreshing the token if necessary.
   */
  async getValidAccessToken() {
    this.lastActivity = Date.now();

    if (this.tokens && this.isTokenValid(this.tokens)) {
      return this.tokens.accessToken;
    }

    if (this.tokens && this.shouldRefreshToken()) {
      const refreshedToken = await this.attemptBackgroundRefresh();
      if (refreshedToken) return refreshedToken;
    }
    
    // If no valid token and refresh fails, there's no valid token to return.
    return null;
  }

  /**
   * Allows other parts of the app to subscribe to authentication state changes.
   */
  onAuthStateChanged(callback) {
    this.authStateCallbacks.push(callback);
    // Immediately notify the new subscriber of the current state.
    callback(this.isSignedIn(), this.getUserInfo());
  }

  /**
   * Notifies all subscribers about a change in authentication state.
   */
  notifyAuthStateChange(isSignedIn) {
    this.authStateCallbacks.forEach(callback => {
      try {
        callback(isSignedIn, this.getUserInfo());
      } catch (error) {
        console.error('Auth state callback error:', error);
      }
    });
  }
}

export const googleAuthService = new GoogleAuthService();